// components/EvaluationTimeline.js
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Card, Badge, ButtonGroup, Button } from 'react-bootstrap';

/**
 * Standalone horizontal timeline for all AI evaluations
 * Sits as a separate row in TradingCommand for maximum visibility
 * Shows position entry/exit markers overlaid on evaluation timeline
 */
const EvaluationTimeline = ({ predictions, onEvaluationSelect, selectedEvalId, positions, onPositionSelect }) => {
  const timelineRef = useRef(null);
  const [hoveredEval, setHoveredEval] = useState(null);
  const [hoveredPosition, setHoveredPosition] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [filterType, setFilterType] = useState('all');
  const [filterDecision, setFilterDecision] = useState('all');
  
  // Process predictions into evaluations
  const allEvaluations = useMemo(() => {
    if (!predictions || predictions.length === 0) return [];

    const evals = predictions
      .map((bar, index) => {
        const decision = bar.eval_decision || bar.decision || bar.signal;
        if (!decision || decision === '') return null;

        const reasoning = bar.eval_reasoning || 
                         bar.eval_narrative_summary ||
                         bar.reasoning || 
                         bar.gen_ai_msg?.Reason || 
                         '';
        
        if (!reasoning || reasoning.trim() === '') return null;

        let evalType = bar.eval_type;
        if (!evalType || evalType === '' || evalType === 'UNKNOWN') {
          const hasPositionId = bar.eval_position_id || bar.position_id;
          evalType = hasPositionId && hasPositionId !== '' ? "REACTIVE" : "PROACTIVE";
        }

        return {
          id: `eval-${index}-${bar.t}`,
          type: evalType,
          decision: decision,
          reasoning: reasoning,
          timestamp: bar.t || bar.timestamp,
          confidence: bar.eval_confidence || bar.confidence,
          execution_time: bar.eval_execution_time,
          position_id: bar.eval_position_id || bar.position_id,
          exit_risk: bar.eval_exit_risk,
          fullData: bar,
        };
      })
      .filter(e => e !== null);

    return evals;
  }, [predictions]);

  // Process positions into markers - USE ORIGINAL LOGIC
  const positionMarkers = useMemo(() => {
    if (!positions || positions.length === 0) return [];
    
    return positions.map(pos => {
      // Get P&L and isWin DIRECTLY from position object (as in original)
      const pnl = pos.unrealizedPL || pos.pl_delta || 0;
      const isWin = pos.isWin; // USE ORIGINAL isWin from position object
      
      console.log(`📊 Position ${pos.symbol}: pnl=${pnl}, isWin=${isWin}, unrealizedPL=${pos.unrealizedPL}`);
      
      return {
        id: pos.id || pos.a_id || `pos-${pos.group}`,
        entryTime: pos.entryTime,
        exitTime: pos.exitTime,
        entryPrice: pos.entryPrice,
        exitPrice: pos.exitPrice || pos.currentPrice,
        symbol: pos.symbol,
        isLong: pos.isLong,
        qty: pos.qty,
        pnl: pnl,
        isWin: isWin, // Use original isWin
        isClosed: !!pos.exitTime,
        position: pos
      };
    });
  }, [positions]);

  // Calculate stats
  const stats = useMemo(() => {
    const decisions = {};
    allEvaluations.forEach(e => {
      decisions[e.decision] = (decisions[e.decision] || 0) + 1;
    });
    
    return {
      total: allEvaluations.length,
      proactive: allEvaluations.filter(e => e.type === 'PROACTIVE').length,
      reactive: allEvaluations.filter(e => e.type === 'REACTIVE').length,
      decisions
    };
  }, [allEvaluations]);

  // Filter evaluations
  const filteredEvaluations = useMemo(() => {
    return allEvaluations.filter(e => {
      if (filterType !== 'all' && e.type.toLowerCase() !== filterType) return false;
      if (filterDecision !== 'all' && e.decision !== filterDecision) return false;
      return true;
    });
  }, [allEvaluations, filterType, filterDecision]);

  // Find entry point (first reactive evaluation)
  const entryPoint = useMemo(() => {
    return allEvaluations.find(e => e.type === 'REACTIVE');
  }, [allEvaluations]);

  // Available decisions for filter buttons
  const availableDecisions = useMemo(() => {
    return Object.keys(stats.decisions).sort();
  }, [stats.decisions]);

  // Calculate time range - only after hooks
  const startTime = allEvaluations.length > 0 
    ? new Date(filteredEvaluations[0]?.timestamp || allEvaluations[0].timestamp)
    : new Date();
  const endTime = allEvaluations.length > 0 
    ? new Date(filteredEvaluations[filteredEvaluations.length - 1]?.timestamp || allEvaluations[allEvaluations.length - 1].timestamp)
    : new Date();
  const totalDuration = endTime - startTime || 1;

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart(e.pageX - timelineRef.current.offsetLeft);
    setScrollLeft(timelineRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - timelineRef.current.offsetLeft;
    const walk = (x - dragStart) * 2;
    timelineRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Format time
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Get decision icon
  const getDecisionIcon = (decision) => {
    switch(decision) {
      case 'PROCEED': return '✓';
      case 'SKIP': return '⊗';
      case 'ADJUST': return '🔄';
      case 'HOLD': return '⏸';
      case 'CLOSE': return '🚪';
      default: return '•';
    }
  };

  // Get decision color - DARK THEME COLORS
  const getDecisionColor = (decision) => {
    switch(decision) {
      case 'PROCEED': return '#00ff41';
      case 'SKIP': return '#888';
      case 'ADJUST': return '#ffd700';
      case 'HOLD': return '#00d4ff';
      case 'CLOSE': return '#ff3366';
      default: return '#888';
    }
  };

  // Get phase color - DARK THEME COLORS
  const getPhaseColor = (phase) => {
    return phase === 'PROACTIVE' ? '#888' : '#00bfff';
  };

  // Calculate position percentage
  const getPositionPercent = (timestamp) => {
    const evalTime = new Date(timestamp);
    const elapsed = evalTime - startTime;
    return (elapsed / totalDuration) * 100;
  };

  // Handle marker click
  const handleMarkerClick = (evaluation, e) => {
    e.stopPropagation();
    if (onEvaluationSelect) {
      onEvaluationSelect(evaluation);
    }
  };

  // Handle marker hover
  const handleMarkerHover = (evaluation, e) => {
    setHoveredEval(evaluation);
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  // Handle position marker click
  const handlePositionClick = (positionMarker, e) => {
    e.stopPropagation();
    if (onPositionSelect) {
      onPositionSelect(positionMarker.position);
    }
  };

  // Handle position marker hover
  const handlePositionHover = (positionMarker, e) => {
    setHoveredPosition(positionMarker);
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  // Calculate if position marker is in view
  const isPositionInView = (timestamp) => {
    if (!timestamp) return false;
    const time = new Date(timestamp);
    return time >= startTime && time <= endTime;
  };

  // Early return AFTER all hooks
  if (allEvaluations.length === 0) {
    return (
      <Card className="mb-3" style={{ backgroundColor: '#000', border: '1px solid #333' }}>
        <Card.Body className="text-center py-3" style={{ color: '#888' }}>
          <small>No evaluations available. Evaluations appear during market hours.</small>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-3" style={{ backgroundColor: '#000', border: '1px solid #333' }}>
      <Card.Body className="py-2 px-3">
        {/* Header with Filters */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-3">
            <div>
              <strong style={{ color: '#fff' }}>AI Evaluation Timeline</strong>
              <div style={{ fontSize: '11px', color: '#888' }}>
                {formatTime(startTime)} - {formatTime(endTime)}
              </div>
            </div>

            {/* Type Filter */}
            <ButtonGroup size="sm">
              <Button 
                variant={filterType === 'all' ? 'light' : 'outline-light'}
                onClick={() => setFilterType('all')}
              >
                All ({stats.total})
              </Button>
              <Button 
                variant={filterType === 'proactive' ? 'light' : 'outline-light'}
                onClick={() => setFilterType('proactive')}
              >
                Proactive ({stats.proactive})
              </Button>
              <Button 
                variant={filterType === 'reactive' ? 'light' : 'outline-light'}
                onClick={() => setFilterType('reactive')}
              >
                Reactive ({stats.reactive})
              </Button>
            </ButtonGroup>
          </div>

          {/* Decision Filter */}
          <div className="d-flex align-items-center gap-2">
            <small style={{ color: '#888' }}>Filter by:</small>
            <ButtonGroup size="sm">
              <Button 
                variant={filterDecision === 'all' ? 'light' : 'outline-light'}
                onClick={() => setFilterDecision('all')}
                style={{ fontSize: '10px' }}
              >
                All
              </Button>
              {availableDecisions.map(dec => (
                <Button 
                  key={dec}
                  variant={filterDecision === dec ? 'light' : 'outline-light'}
                  onClick={() => setFilterDecision(dec)}
                  style={{ 
                    fontSize: '10px',
                    color: filterDecision === dec ? '#000' : getDecisionColor(dec),
                    borderColor: getDecisionColor(dec)
                  }}
                >
                  {getDecisionIcon(dec)} {dec} ({stats.decisions[dec]})
                </Button>
              ))}
            </ButtonGroup>
          </div>
        </div>

        {/* Timeline Container */}
        <div
          ref={timelineRef}
          style={{
            overflowX: 'auto',
            overflowY: 'visible',
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            paddingBottom: '50px',
            paddingTop: '50px',
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Timeline Track - DARK THEME */}
          <div style={{ 
            position: 'relative', 
            height: '120px',
            minWidth: `${Math.max(1000, filteredEvaluations.length * 80)}px`,
            borderBottom: '3px solid #444',
            background: 'linear-gradient(to bottom, #1a1a1a 0%, #000000 100%)'
          }}>
            {/* Time markers (every 5 minutes) */}
            {(() => {
              const markers = [];
              const interval = 5 * 60 * 1000;
              let current = new Date(Math.ceil(startTime.getTime() / interval) * interval);
              
              while (current <= endTime) {
                const percent = getPositionPercent(current);
                markers.push(
                  <div
                    key={current.getTime()}
                    style={{
                      position: 'absolute',
                      left: `${percent}%`,
                      top: '0',
                      height: '100%',
                      borderLeft: '1px dashed #555'
                    }}
                  >
                    <small 
                      style={{
                        position: 'absolute',
                        top: '-20px',
                        left: '-20px',
                        fontSize: '10px',
                        color: '#aaa',
                        fontWeight: '500'
                      }}
                    >
                      {formatTime(current)}
                    </small>
                  </div>
                );
                current = new Date(current.getTime() + interval);
              }
              return markers;
            })()}

            {/* Position Entry/Exit markers - ABOVE the timeline */}
            {positionMarkers.length > 0 && positionMarkers.map((marker) => {
              const entryPercent = getPositionPercent(marker.entryTime);
              const exitPercent = marker.exitTime ? getPositionPercent(marker.exitTime) : null;
              
              if (!isPositionInView(marker.entryTime)) return null;

              return (
                <React.Fragment key={marker.id}>
                  {/* Entry Marker - ABOVE timeline */}
                  <div
                    style={{
                      position: 'absolute',
                      left: `${entryPercent}%`,
                      top: '-25px',
                      transform: 'translateX(-50%)',
                      cursor: 'pointer',
                      zIndex: 30
                    }}
                    onClick={(e) => handlePositionClick(marker, e)}
                    onMouseEnter={(e) => handlePositionHover(marker, e)}
                    onMouseLeave={() => setHoveredPosition(null)}
                  >
                    {/* Entry arrow pointing down */}
                    <div
                      style={{
                        width: '0',
                        height: '0',
                        borderLeft: '8px solid transparent',
                        borderRight: '8px solid transparent',
                        borderTop: `12px solid ${marker.isLong ? '#00ff41' : '#ff3366'}`,
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: '-18px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        color: marker.isLong ? '#00ff41' : '#ff3366',
                        whiteSpace: 'nowrap',
                        backgroundColor: 'rgba(0,0,0,0.95)',
                        padding: '2px 4px',
                        borderRadius: '3px'
                      }}
                    >
                      {marker.isLong ? '🟢 LONG' : '🔴 SHORT'} {marker.qty}
                    </div>
                  </div>

                  {/* Vertical line connecting entry to timeline */}
                  <div
                    style={{
                      position: 'absolute',
                      left: `${entryPercent}%`,
                      top: '-13px',
                      width: '2px',
                      height: '18px',
                      backgroundColor: marker.isLong ? '#00ff41' : '#ff3366',
                      zIndex: 25
                    }}
                  />

                  {/* Exit Marker - ABOVE timeline (if closed) */}
                  {exitPercent && marker.isClosed && (
                    <>
                      <div
                        style={{
                          position: 'absolute',
                          left: `${exitPercent}%`,
                          top: '-25px',
                          transform: 'translateX(-50%)',
                          cursor: 'pointer',
                          zIndex: 30
                        }}
                        onClick={(e) => handlePositionClick(marker, e)}
                        onMouseEnter={(e) => handlePositionHover(marker, e)}
                        onMouseLeave={() => setHoveredPosition(null)}
                      >
                        {/* Exit flag */}
                        <div
                          style={{
                            width: '0',
                            height: '0',
                            borderLeft: '8px solid transparent',
                            borderRight: '8px solid transparent',
                            borderTop: `12px solid ${marker.isWin ? '#00ff41' : '#ff3366'}`,
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                            opacity: 0.8
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            top: '-18px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            color: marker.isWin ? '#00ff41' : '#ff3366',
                            whiteSpace: 'nowrap',
                            backgroundColor: 'rgba(0,0,0,0.95)',
                            padding: '2px 4px',
                            borderRadius: '3px'
                          }}
                        >
                          {marker.isWin ? '✅ EXIT' : '❌ EXIT'} ${Math.abs(marker.pnl).toFixed(0)}
                        </div>
                      </div>

                      {/* Vertical line connecting exit to timeline */}
                      <div
                        style={{
                          position: 'absolute',
                          left: `${exitPercent}%`,
                          top: '-13px',
                          width: '2px',
                          height: '18px',
                          backgroundColor: marker.isWin ? '#00ff41' : '#ff3366',
                          opacity: 0.8,
                          zIndex: 25
                        }}
                      />

                      {/* Position duration bar - ABOVE timeline */}
                      <div
                        style={{
                          position: 'absolute',
                          left: `${entryPercent}%`,
                          top: '-5px',
                          width: `${exitPercent - entryPercent}%`,
                          height: '4px',
                          backgroundColor: marker.isWin ? 'rgba(0,255,65,0.3)' : 'rgba(255,51,102,0.3)',
                          borderRadius: '2px',
                          zIndex: 20
                        }}
                      />
                    </>
                  )}
                </React.Fragment>
              );
            })}

            {/* Entry point separator (if showing all or reactive) */}
            {entryPoint && (filterType === 'all' || filterType === 'reactive') && (
              <div
                style={{
                  position: 'absolute',
                  left: `${getPositionPercent(entryPoint.timestamp)}%`,
                  top: '-5px',
                  height: 'calc(100% + 10px)',
                  borderLeft: '4px solid #ffd700',
                  zIndex: 5
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '-30px',
                    left: '-35px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    color: '#ffd700',
                    backgroundColor: '#000',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    border: '1px solid #ffd700'
                  }}
                >
                  ⚡ ENTRY
                </div>
              </div>
            )}

            {/* Evaluation markers */}
            {filteredEvaluations.map((evaluation, index) => {
              const percent = getPositionPercent(evaluation.timestamp);
              const isSelected = selectedEvalId === evaluation.id;
              
              return (
                <div
                  key={evaluation.id}
                  style={{
                    position: 'absolute',
                    left: `${percent}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    cursor: 'pointer',
                    zIndex: isSelected ? 20 : 10
                  }}
                  onClick={(e) => handleMarkerClick(evaluation, e)}
                  onMouseEnter={(e) => handleMarkerHover(evaluation, e)}
                  onMouseLeave={() => setHoveredEval(null)}
                >
                  {/* Marker circle */}
                  <div
                    style={{
                      width: isSelected ? '20px' : '14px',
                      height: isSelected ? '20px' : '14px',
                      borderRadius: '50%',
                      backgroundColor: getPhaseColor(evaluation.type),
                      border: `3px solid ${getDecisionColor(evaluation.decision)}`,
                      boxShadow: isSelected ? '0 0 10px rgba(255,255,255,0.5)' : 'none',
                      transition: 'all 0.2s'
                    }}
                  />
                  
                  {/* Decision icon */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '-22px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: '12px',
                      color: getDecisionColor(evaluation.decision)
                    }}
                  >
                    {getDecisionIcon(evaluation.decision)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Evaluation Hover Tooltip - DARK THEME */}
        {hoveredEval && !isDragging && (
          <div
            style={{
              position: 'fixed',
              left: `${mousePosition.x + 15}px`,
              top: `${mousePosition.y + 15}px`,
              backgroundColor: '#000',
              border: '2px solid #555',
              borderRadius: '6px',
              padding: '10px 14px',
              boxShadow: '0 4px 12px rgba(255,255,255,0.15)',
              zIndex: 1000,
              pointerEvents: 'none',
              maxWidth: '300px',
              color: '#fff'
            }}
          >
            <div className="d-flex align-items-center gap-2 mb-2">
              <Badge bg="dark" style={{ border: `1px solid ${getPhaseColor(hoveredEval.type)}` }}>
                {hoveredEval.type}
              </Badge>
              <Badge bg="dark" style={{ border: `1px solid ${getDecisionColor(hoveredEval.decision)}` }}>
                {getDecisionIcon(hoveredEval.decision)} {hoveredEval.decision}
              </Badge>
            </div>
            <div className="mb-1">
              <small style={{ color: '#ccc' }}><strong>Time:</strong> {formatTime(hoveredEval.timestamp)}</small>
            </div>
            <small style={{ color: '#aaa' }}>
              {hoveredEval.reasoning.substring(0, 120)}{hoveredEval.reasoning.length > 120 ? '...' : ''}
            </small>
            {hoveredEval.confidence && (
              <div className="mt-2">
                <small style={{ color: '#ccc' }}>Confidence: {hoveredEval.confidence}%</small>
              </div>
            )}
            {hoveredEval.exit_risk && (
              <div className="mt-1">
                <Badge bg={
                  hoveredEval.exit_risk.risk_level === 'LOW' ? 'success' : 
                  hoveredEval.exit_risk.risk_level === 'MED' ? 'warning' : 'danger'
                }>
                  Risk: {hoveredEval.exit_risk.risk_level}
                </Badge>
              </div>
            )}
            <small className="d-block mt-2" style={{ color: '#888', fontStyle: 'italic' }}>
              Click to view full details below
            </small>
          </div>
        )}

        {/* Position Hover Tooltip - DARK THEME */}
        {hoveredPosition && !isDragging && (
          <div
            style={{
              position: 'fixed',
              left: `${mousePosition.x + 15}px`,
              top: `${mousePosition.y + 15}px`,
              backgroundColor: '#000',
              border: '2px solid #555',
              borderRadius: '6px',
              padding: '10px 14px',
              boxShadow: '0 4px 12px rgba(255,255,255,0.15)',
              zIndex: 1000,
              pointerEvents: 'none',
              maxWidth: '300px',
              color: '#fff'
            }}
          >
            <div className="d-flex align-items-center gap-2 mb-2">
              <Badge bg={hoveredPosition.isLong ? 'success' : 'danger'}>
                {hoveredPosition.isLong ? 'LONG' : 'SHORT'}
              </Badge>
              <strong style={{ color: '#fff' }}>{hoveredPosition.symbol}</strong>
              <Badge bg={hoveredPosition.isWin ? 'success' : 'danger'}>
                {hoveredPosition.isWin ? 'WIN' : 'LOSS'}
              </Badge>
            </div>
            <div className="mb-1">
              <small style={{ color: '#ccc' }}><strong>Entry:</strong> ${hoveredPosition.entryPrice.toFixed(2)} @ {new Date(hoveredPosition.entryTime).toLocaleTimeString()}</small>
            </div>
            {hoveredPosition.exitTime && (
              <div className="mb-1">
                <small style={{ color: '#ccc' }}><strong>Exit:</strong> ${hoveredPosition.exitPrice.toFixed(2)} @ {new Date(hoveredPosition.exitTime).toLocaleTimeString()}</small>
              </div>
            )}
            <div className="mb-1">
              <small style={{ color: '#ccc' }}><strong>Quantity:</strong> {hoveredPosition.qty} shares</small>
            </div>
            <div>
              <small style={{ color: '#ccc' }}><strong>P&L:</strong> <span className={hoveredPosition.pnl >= 0 ? 'text-success' : 'text-danger'}>
                {hoveredPosition.pnl >= 0 ? '+' : ''}${hoveredPosition.pnl.toFixed(2)}
              </span></small>
            </div>
            <small className="d-block mt-2" style={{ color: '#888', fontStyle: 'italic' }}>
              Click to view position details
            </small>
          </div>
        )}

        {/* Legend - DARK THEME */}
        <div className="d-flex justify-content-center gap-4 mt-2 pt-2" style={{ fontSize: '11px', borderTop: '1px solid #333' }}>
          <div className="d-flex align-items-center gap-1">
            <div style={{ width: '12px', height: '12px', backgroundColor: '#888', borderRadius: '50%' }}></div>
            <span style={{ color: '#ccc' }}>Proactive</span>
          </div>
          <div className="d-flex align-items-center gap-1">
            <div style={{ width: '12px', height: '12px', backgroundColor: '#00bfff', borderRadius: '50%' }}></div>
            <span style={{ color: '#ccc' }}>Reactive</span>
          </div>
          <span style={{ color: '#666' }}>|</span>
          <span style={{ color: '#00ff41' }}>✓ Proceed</span>
          <span style={{ color: '#888' }}>⊗ Skip</span>
          <span style={{ color: '#ffd700' }}>🔄 Adjust</span>
          <span style={{ color: '#00d4ff' }}>⏸ Hold</span>
          <span style={{ color: '#ff3366' }}>🚪 Close</span>
        </div>
      </Card.Body>
    </Card>
  );
};

export default EvaluationTimeline;